import React, { useRef } from 'react';
import { Image as ImageIcon, Video, FileText as DocIcon, Mic, Play } from 'lucide-react';
import { mediaUrl } from '../../../lib/api';

export const TYPE_ICONS = { photo: ImageIcon, video: Video, document: DocIcon, voice: Mic };

export default function HoverMedia({ v }) {
  const audioRef = useRef(null);
  if (v.url && v.type === 'photo') {
    return <img src={mediaUrl(v.url)} alt={v.name} className="w-full h-full object-cover" loading="lazy" />;
  }
  if (v.type === 'video') {
    // Offloaded videos: v.url is a POSTER .jpg (not a playable file) — a <video> with
    // a .jpg src renders nothing, which is why the grid showed no preview. Show the
    // poster as an <img> instead. We also DON'T load the real (offloaded) video in the
    // grid: that would cost proxy bandwidth. Clicking the tile opens the lightbox to
    // actually play it.
    if (v.tg_stored) {
      return (
        <div className="w-full h-full relative">
          <img src={mediaUrl(`/api/vault/${v.id}/thumb`)} alt={v.name} loading="lazy"
            className="w-full h-full object-cover"
            onError={e => { e.currentTarget.style.display = 'none'; }} />
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="w-9 h-9 rounded-full bg-black/45 backdrop-blur-sm flex items-center justify-center border border-white/30">
              <Play size={14} className="text-white ml-0.5" fill="currentColor" />
            </span>
          </span>
        </div>
      );
    }
    // Local (not yet offloaded) video: hover to play the real local file.
    if (v.url) {
      return (
        <div className="w-full h-full relative group/vid">
          <video src={mediaUrl(v.url)} muted loop playsInline preload="metadata" className="w-full h-full object-cover"
            onMouseEnter={e => e.target.play().catch(() => {})} onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }} />
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover/vid:opacity-0 transition-opacity">
            <span className="flex items-center gap-1.5 text-[10px] text-slate-200 bg-black/60 px-2.5 py-1.5 rounded-lg border border-white/10"><Play size={10} /> Hover to play</span>
          </span>
        </div>
      );
    }
  }
  if (v.url && v.type === 'voice') {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 cursor-pointer"
        onMouseEnter={() => audioRef.current?.play().catch(() => {})}
        onMouseLeave={() => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; } }}>
        <audio ref={audioRef} src={mediaUrl(v.url)} preload="none" />
        <Mic size={26} className="text-indigo-400" />
        <span className="text-[10px] text-slate-400 bg-black/50 px-2 py-1 rounded-lg border border-white/10">Hover to play</span>
      </div>
    );
  }
  const Ic = TYPE_ICONS[v.type] || ImageIcon;
  return <div className="w-full h-full flex items-center justify-center"><Ic size={30} className="text-white/25" /></div>;
}
